/**
 * Price utilities for card collection
 * Handles currency conversion and formatting
 */
import { CURRENCY_SYMBOLS } from '@/lib/settingsUtils';
import type { CardPrices, Currency, EnrichedCollectionCard, Finish } from '@/types/card';

/** Map finish to price field suffix */
const FINISH_SUFFIX: Record<Finish, '' | '_foil' | '_etched'> = {
  nonfoil: '',
  foil: '_foil',
  etched: '_etched',
};

/**
 * Get the price for a specific currency and finish from a prices object.
 * Strict field lookup — no fallback to other finishes.
 * Returns null if the price data is missing.
 */
export function getPrice(prices: CardPrices, currency: 'usd' | 'eur', finish: Finish): number | null {
  const field = `${currency}${FINISH_SUFFIX[finish]}` as keyof CardPrices;
  const priceStr = prices[field];
  if (priceStr == null) return null;
  const num = parseFloat(priceStr);
  return isNaN(num) ? null : num;
}

/**
 * Get the nonfoil base price from a raw prices object based on currency.
 * Used for deck cards and other places where we don't have finish info.
 */
export function getSimplePriceFromPrices(prices: CardPrices | undefined | null, currency: Currency = 'USD'): number | null {
  if (!prices) return null;
  return getPrice(prices, currency.toLowerCase() as 'usd' | 'eur', 'nonfoil');
}

/**
 * Get the nonfoil base price for an enriched collection card.
 * For finish-aware pricing, use pre-computed price_usd/price_eur or call getPrice directly.
 */
export function getCardPrice(card: EnrichedCollectionCard, currency: Currency = 'USD'): number {
  const price = getPrice(card.card.prices, currency.toLowerCase() as 'usd' | 'eur', 'nonfoil');
  return price ?? 0;
}

/**
 * Format a price value with the appropriate currency symbol
 */
export function formatPrice(value: number, currency: Currency = 'USD'): string {
  const symbol = CURRENCY_SYMBOLS[currency];
  const locale = currency === 'EUR' ? 'de-DE' : 'en-US';

  const formatted = value.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // EUR: symbol on right with space (e.g., "0,69 €")
  // USD: symbol on left (e.g., "$0.69")
  if (currency === 'EUR') {
    return `${formatted} ${symbol}`;
  }
  return `${symbol}${formatted}`;
}

/**
 * Format currency for display with full currency formatting
 */
export function formatCurrency(value: number, currency: Currency = 'USD'): string {
  const locale = currency === 'EUR' ? 'de-DE' : 'en-US';

  return value.toLocaleString(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format a user-entered purchase price with the correct currency symbol.
 * Uses Intl.NumberFormat for locale-aware formatting of all supported purchase currencies.
 * Accepts string currency code since purchase_currency is string | null in the DB.
 */
export function formatPurchasePrice(value: number, currency: string | null | undefined): string {
  const code = currency ?? 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
    }).format(value);
  } catch {
    // Fallback for unknown currency codes
    return `${value.toFixed(2)} ${code}`;
  }
}

/**
 * Clean a price being typed: keeps digits and a single `.` or `,` decimal separator.
 * Returns null when the text can't become a price (e.g. two separators), so callers keep the previous value.
 */
export function sanitizePriceInput(text: string): string | null {
  const cleaned = text.replace(/[^\d.,]/g, '');
  const separatorCount = (cleaned.match(/[.,]/g) ?? []).length;
  return separatorCount > 1 ? null : cleaned;
}

/** Parse a user-entered price accepting both `7.35` and `7,35`. Empty or invalid text returns null. */
export function parsePriceInput(text: string): number | null {
  const sanitized = sanitizePriceInput(text.trim());
  if (!sanitized) return null;
  const value = Number.parseFloat(sanitized.replace(',', '.'));
  return Number.isFinite(value) ? value : null;
}

/**
 * Calculate total value of cards using pre-computed finish-aware prices
 */
export function calculateTotalValue(cards: EnrichedCollectionCard[], currency: Currency = 'USD'): number {
  return cards.reduce((sum, card) => {
    const price = currency === 'EUR' ? card.price_eur : card.price_usd;
    return sum + (price ?? 0) * card.quantity;
  }, 0);
}
